// 1:1-Portierung von scripts/assistant-questions/aggregate.js im Repo
// aivy-backend-app (Branch develop). Reine Funktion ohne AWS-Abhängigkeit —
// deshalb direkt übernehmbar. Quelle der Wahrheit und Tests liegen dort
// (aggregate.test.js); bei einer inhaltlichen Änderung dort BEIDE Dateien
// synchron halten, diese hier hat keine eigene Test-Suite.
//
// Zweck: Häufigkeitsauswertung des Fragen-Logs des Dashboard-Assistenten
// (Kriterium 3 des Assistenten-Rocks). Items kommen aus der Query
// `assistantQuestionsLog` (sparser GSI auf der Log-Tabelle) — `item.data`
// muss VOR dem Aufruf mit JSON.parse geparst werden, AWSJSON liefert einen
// String.

const EVENT_NAME = 'ASSISTANT_QUESTION'

/** Entitäts-Referenzen auf ihre Art reduzieren, damit Fragen clustern. */
const MENTION = /@(career|space):[0-9a-zA-Z_-]+(?:\.[0-9a-zA-Z_-]+)*/g

/**
 * Bringt zwei gleich gemeinte Fragen auf dieselbe Zeichenkette.
 *
 * `@career:<id>` wird zu `@career`: sonst zersplittert dieselbe Frage in so
 * viele Einzelfälle, wie es Stellen gibt — genau der Grund, warum Kunden
 * Entitäten per `@` nennen sollen und nicht als Klartext.
 *
 * Zahlen bleiben stehen. "was bedeutet 48 von 100" und "was bedeutet 72 von 100"
 * sind zwar dieselbe Frage, aber das zu erkennen wäre Auslegung — und ein
 * Auswertungswerkzeug soll nicht interpretieren.
 */
export const normaliseQuestion = (text) => {
  if (typeof text !== 'string') return null

  const normalised = text
    .replace(MENTION, '@$1')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/[?!.\s]+$/, '')

  return normalised.length > 0 ? normalised : null
}

/** Absteigend nach Anzahl, bei Gleichstand alphabetisch — stabile Ausgabe. */
const byCountThenName = (a, b) =>
  b.count - a.count || String(a.wert).localeCompare(String(b.wert))

const zaehle = (eintraege, schluessel) => {
  const counts = new Map()

  eintraege.forEach((eintrag) => {
    const wert = schluessel(eintrag)
    if (wert === null || wert === undefined) return
    counts.set(wert, (counts.get(wert) || 0) + 1)
  })

  return Array.from(counts, ([wert, count]) => ({ wert, count })).sort(
    byCountThenName
  )
}

/**
 * @param {Array<object>} items Log-Zeilen, `data` bereits als Objekt (nicht
 *   mehr als AWSJSON-String).
 * @returns {object} Häufigkeiten nach Frage, Screen, Rolle und Wissensherkunft
 *   plus Zeitraum.
 */
export const aggregate = (items) => {
  const fragen = (Array.isArray(items) ? items : [])
    .filter((item) => item?.event_name === EVENT_NAME)
    .map((item) => ({
      normalisiert: normaliseQuestion(item?.data?.question),
      original: item?.data?.question,
      screen: item?.data?.screen ?? null,
      role: item?.data?.role ?? null,
      mentions: item?.data?.mention_types ?? null,
      wissen: item?.data?.knowledge_source ?? null,
      wissenGrund: item?.data?.knowledge_reason ?? null,
      createdAt: item?.createdAt ?? null
    }))
    .filter(({ normalisiert }) => normalisiert !== null)

  // Das erste Original je Cluster mitgeben: die normalisierte Form ist zum
  // Zählen gut und zum Lesen schlecht.
  const beispiele = new Map()
  fragen.forEach(({ normalisiert, original }) => {
    if (!beispiele.has(normalisiert)) beispiele.set(normalisiert, original)
  })

  const zeitpunkte = fragen
    .map(({ createdAt }) => createdAt)
    .filter(Boolean)
    .sort()

  return {
    gesamt: fragen.length,
    zeitraum:
      zeitpunkte.length > 0
        ? { von: zeitpunkte[0], bis: zeitpunkte[zeitpunkte.length - 1] }
        : null,
    nachFrage: zaehle(fragen, (f) => f.normalisiert).map(({ wert, count }) => ({
      frage: wert,
      beispiel: beispiele.get(wert),
      count
    })),
    nachScreen: zaehle(fragen, (f) => f.screen).map(({ wert, count }) => ({
      screen: wert,
      count
    })),
    nachRolle: zaehle(fragen, (f) => f.role).map(({ wert, count }) => ({
      role: wert,
      count
    })),
    mitEntitaet: fragen.filter(
      ({ mentions }) => Array.isArray(mentions) && mentions.length > 0
    ).length,

    // DIE Zahl, die die Redaktionsarbeit steuert. `null` heisst "vor der
    // Einfuehrung des Felds geloggt" und nicht "nichts gefunden" — die beiden
    // zu vermischen wuerde die Abdeckung schlechter aussehen lassen, als sie
    // ist. `unbekannt` und nicht weggelassen: `zaehle` ueberspringt leere
    // Werte, sonst fielen aeltere Zeilen still aus der Summe.
    nachWissen: zaehle(fragen, (f) => f.wissen ?? 'unbekannt').map(
      ({ wert, count }) => ({
        wissen: wert,
        count
      })
    ),

    // Nur fuer die Faelle OHNE Wissen, und dort ist der Grund die eigentliche
    // Aussage: `no_content` ist eine Inhaltsluecke und damit ein Auftrag,
    // `unknown_frontend` ein Konfigurationsfehler und damit ein Bug.
    ohneWissen: zaehle(
      fragen.filter(({ wissen }) => wissen === 'none'),
      (f) => f.wissenGrund
    ).map(({ wert, count }) => ({ grund: wert, count }))
  }
}
